import { User, Phone, Mail, MapPin, Calendar, Heart, Pill, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { differenceInYears, parseISO } from "date-fns";

interface PatientHeaderProps {
  patient: {
    name: string;
    date_of_birth: string;
    gender: string;
    phone: string;
    email: string;
    address: string;
  };
  medicationsCount: number;
  allergiesCount: number;
  childrenCount: number;
  appointmentsCount: number;
}

export function PatientHeader({
  patient,
  medicationsCount,
  allergiesCount,
  childrenCount,
  appointmentsCount,
}: PatientHeaderProps) {
  const getAge = () => {
    if (!patient.date_of_birth) return null;
    try {
      return differenceInYears(new Date(), parseISO(patient.date_of_birth));
    } catch {
      return null;
    }
  };

  const getGenderLabel = (gender: string) => {
    const labels: Record<string, string> = {
      femme: "Femme",
      homme: "Homme",
      intersexe: "Intersexe",
      prefere_ne_pas_repondre: "Non précisé",
    };
    return labels[gender] || "Non renseigné";
  };

  const getGenderColor = (gender: string) => {
    const colors: Record<string, string> = {
      femme: "bg-pink-100 text-pink-700 border-pink-200",
      homme: "bg-blue-100 text-blue-700 border-blue-200",
      intersexe: "bg-purple-100 text-purple-700 border-purple-200",
    };
    return colors[gender] || "bg-muted text-muted-foreground";
  };

  const age = getAge();

  return (
    <div className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 rounded-xl sm:rounded-2xl border shadow-lg overflow-hidden">
      {/* Header band */}
      <div className="bg-gradient-to-r from-primary to-primary/80 px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
            <User className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-white truncate">{patient.name || "Nouveau consultant"}</h1>
            <div className="flex items-center gap-2 sm:gap-3 mt-1 flex-wrap">
              {patient.gender && (
                <Badge className={`${getGenderColor(patient.gender)} border text-[10px] sm:text-xs`}>
                  {getGenderLabel(patient.gender)}
                </Badge>
              )}
              {age !== null && (
                <span className="text-white/80 text-xs sm:text-sm">{age} ans</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info grid */}
      <div className="p-3 sm:p-6">
        <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-4 sm:mb-6">
          {patient.phone && (
            <div className="flex items-center gap-2 text-xs sm:text-sm">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-muted-foreground text-[10px] sm:text-xs">Téléphone</p>
                <p className="font-medium text-xs sm:text-sm truncate">{patient.phone}</p>
              </div>
            </div>
          )}
          {patient.email && (
            <div className="flex items-center gap-2 text-xs sm:text-sm">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-muted-foreground text-[10px] sm:text-xs">Email</p>
                <p className="font-medium text-xs sm:text-sm truncate">{patient.email}</p>
              </div>
            </div>
          )}
          {patient.address && (
            <div className="flex items-center gap-2 text-xs sm:text-sm">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-muted-foreground text-[10px] sm:text-xs">Adresse</p>
                <p className="font-medium text-xs sm:text-sm truncate">{patient.address}</p>
              </div>
            </div>
          )}
          {patient.date_of_birth && (
            <div className="flex items-center gap-2 text-xs sm:text-sm">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-muted-foreground text-[10px] sm:text-xs">Naissance</p>
                <p className="font-medium text-xs sm:text-sm">
                  {new Date(patient.date_of_birth).toLocaleDateString("fr-FR")}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-4 gap-2 sm:gap-3">
          <div className="bg-white/50 dark:bg-card rounded-lg sm:rounded-xl p-2 sm:p-4 text-center border">
            <div className="w-8 h-8 sm:w-10 sm:h-10 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-1 sm:mb-2">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <p className="text-lg sm:text-2xl font-bold text-primary">{appointmentsCount}</p>
            <p className="text-[9px] sm:text-xs text-muted-foreground">RDV</p>
          </div>
          <div className="bg-white/50 dark:bg-card rounded-lg sm:rounded-xl p-2 sm:p-4 text-center border">
            <div className="w-8 h-8 sm:w-10 sm:h-10 mx-auto rounded-full bg-purple-100 flex items-center justify-center mb-1 sm:mb-2">
              <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            </div>
            <p className="text-lg sm:text-2xl font-bold text-primary">{childrenCount}</p>
            <p className="text-[9px] sm:text-xs text-muted-foreground">Enfant(s)</p>
          </div>
          <div className="bg-white/50 dark:bg-card rounded-lg sm:rounded-xl p-2 sm:p-4 text-center border">
            <div className="w-8 h-8 sm:w-10 sm:h-10 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-1 sm:mb-2">
              <Pill className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
            </div>
            <p className="text-lg sm:text-2xl font-bold text-primary">{medicationsCount}</p>
            <p className="text-[9px] sm:text-xs text-muted-foreground">Médic.</p>
          </div>
          <div className="bg-white/50 dark:bg-card rounded-lg sm:rounded-xl p-2 sm:p-4 text-center border">
            <div className="w-8 h-8 sm:w-10 sm:h-10 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-1 sm:mb-2">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <p className="text-lg sm:text-2xl font-bold text-primary">{allergiesCount}</p>
            <p className="text-[9px] sm:text-xs text-muted-foreground">Allergie(s)</p>
          </div>
        </div>
      </div>
    </div>
  );
}
